import { Component } from 'preact';
import { GOLD, TEXT_PRIMARY, TEXT_MUTED } from '../../lib/medievalTheme';
import { TIERS, TIER_LABELS, formatRank } from '../../utils/arena/rankUtils';
import { levelFromXp } from '../../utils/arena/profileDefaults';
import { resolveAvatarUrl } from '../../utils/arena/avatarUtils';
import AvatarImage from '../ui/AvatarImage';
import DiscordHandlePill from '../ui/DiscordHandlePill';
import PlayerTitle from '../PlayerTitle';
import { t } from '../../localization/t';
import { isFreePlay } from '../../utils/freePlay';

const TIER_HEX = {
  apprentice: '#a8a29e',
  journeyman: '#d4d4d8',
  adept: '#38bdf8',
  expert: '#a78bfa',
  master: '#fbbf24',
  grandmaster: '#f87171',
};

function PlayerAvatar({ url, size, isHost }) {
  const ringColor = isHost ? `${GOLD} 0.6)` : 'rgba(255,255,255,0.18)';
  return (
    <div
      className="shrink-0 rounded-full overflow-hidden relative"
      style={{
        width: size,
        height: size,
        background: 'rgba(20,16,10,0.85)',
        boxShadow: `0 0 0 1px ${ringColor}, 0 2px 6px rgba(0,0,0,0.5)`,
      }}
    >
      {url ? (
        <AvatarImage src={url} className="w-full h-full" />
      ) : (
        <div
          className="absolute inset-0 flex items-center justify-center text-[0.875cqw] font-bold"
          style={{ color: 'rgba(255,255,255,0.4)' }}
        >
          ?
        </div>
      )}
    </div>
  );
}

function MetaSep() {
  return (
    <span
      aria-hidden="true"
      className="shrink-0 rounded-full"
      style={{
        width: '0.18cqw',
        height: '0.18cqw',
        background: 'rgba(212,168,67,0.4)',
      }}
    />
  );
}

function PlayerMeta({ tier, division, level }) {
  const hex = TIER_HEX[tier];
  const isRanked = !!tier && !!hex;
  const rankColor = isRanked ? hex : '#A6A09B';
  const rankLabel = isRanked ? formatRank(tier, division) : t('Unranked');

  return (
    <div
      className="flex flex-wrap items-center min-w-0"
      style={{ columnGap: '0.55cqw', rowGap: '0.15cqw' }}
    >
      {isFreePlay() ? null : (
        <>
          <span
            className="shrink-0 text-[0.5625cqw] font-bold"
            style={{ color: TEXT_MUTED, textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}
            title={t('Account level {n}', { n: level })}
          >
            {t('Lvl {n}', { n: level })}
          </span>
          <MetaSep />
        </>
      )}
      <span
        className="shrink-0 inline-flex items-center gap-1 text-[0.5625cqw] font-bold"
        style={{ color: rankColor, textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}
      >
        <span
          aria-hidden="true"
          className="rounded-full"
          style={{
            width: '0.375cqw',
            height: '0.375cqw',
            background: rankColor,
            boxShadow: `0 0 4px ${rankColor}`,
          }}
        />
        {rankLabel}
      </span>
    </div>
  );
}

/**
 * Single-participant chip used inside MatchupDisplay. Renders an
 * avatar bubble plus a stack of name + rank + level + (optional)
 * discord handle. Hosts get a gold ring + "Host" tag so the headline
 * player is visually anchored.
 *
 * The `participant` payload mirrors the shape returned by the server's
 * /instances queries (name, profileAvatar, tier, division, xp,
 * discordHandle, role). When `null`, renders an "open seat" placeholder
 * for matches still in `gathering` phase.
 */
export default class PlayerChip extends Component {
  state = { hasTitle: false };

  render() {
    const { participant, sorceryCards, isHost, avatarSize = '2.5cqw' } = this.props;
    if (!participant) {
      return (
        <div className="flex items-center gap-2 min-w-0 opacity-60">
          <div
            className="shrink-0 rounded-full"
            style={{
              width: avatarSize,
              height: avatarSize,
              background: 'rgba(255,255,255,0.04)',
              border: '1px dashed rgba(255,255,255,0.18)',
            }}
          />
          <div className="flex flex-col gap-0.5 min-w-0">
            <span
              className="text-[0.8125cqw] italic font-medium truncate"
              style={{ color: TEXT_MUTED }}
            >
              {t('Open seat')}
            </span>
            <span className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>
              {t('Waiting…')}
            </span>
          </div>
        </div>
      );
    }

    const avatarUrl = resolveAvatarUrl(participant, sorceryCards);
    const level = levelFromXp(participant.xp);
    const name = participant.name || t('Unknown');

    return (
      <div className="flex items-center gap-2 min-w-0">
        <PlayerAvatar url={avatarUrl} size={avatarSize} isHost={isHost} />
        <div className="flex flex-col gap-0.5 min-w-0">
          {/* Top row: name + optional title in a vertical block on the
              left, Discord handle as a sibling column on the right.
              `items-center` on this row centers the Discord handle
              against the name+title namespace block regardless of
              whether a title is present. */}
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex flex-col min-w-0 flex-1">
              <div className="flex items-baseline gap-1.5 min-w-0">
                <span
                  className="font-semibold truncate text-[0.8125cqw] min-w-0"
                  style={{ color: TEXT_PRIMARY, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                >
                  {name}{this.state.hasTitle ? ',' : ''}
                </span>
                {isHost ? (
                  <span
                    className="shrink-0 text-[0.5cqw] font-bold"
                    style={{
                      color: '#f4d788',
                      textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                    }}
                  >
                    {t('Host')}
                  </span>
                ) : null}
              </div>
              <PlayerTitle
                player={{
                  // Participant payloads from /instances queries carry the id
                  // as `userId` (matches the SQL join); matchmaking and other
                  // call sites use `id`. Accept either so a title shows up
                  // wherever PlayerChip is rendered.
                  id: participant.id || participant.userId,
                  displayedCrownAvatarId: participant.displayedCrownAvatarId,
                  isFounder: !!participant.isFounder,
                  isQaTester: !!participant.isQaTester,
                }}
                sorceryCards={sorceryCards}
                prefix=""
                style={{
                  display: 'block',
                  fontSize: '0.6cqw',
                  lineHeight: 1.1,
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  marginTop: '-0.2cqw',
                  color: TEXT_PRIMARY,
                }}
                onResolve={(has) => {
                  if (has !== this.state.hasTitle) this.setState({ hasTitle: has });
                }}
              />
            </div>
            {participant.discordHandle ? (
              <DiscordHandlePill
                handle={participant.discordHandle}
                size="sm"
                withAt
                chrome="text"
                className="shrink-0"
              />
            ) : null}
          </div>
          <PlayerMeta
            tier={participant.tier}
            division={participant.division}
            level={level}
          />
        </div>
      </div>
    );
  }
}

export { TIERS, TIER_LABELS };
